/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { Formik, Form } from 'formik';
// Authentication layout components
import BasicLayout from 'layouts/authentication/components/BasicLayout';
import UserInfo from 'layouts/authentication/sign-in/basic/components/SignInForm';
import initialValues from 'layouts/authentication/sign-in/basic/schemas/initialValues';
import validations from 'layouts/authentication/sign-in/basic/schemas/validations';
import form from 'layouts/authentication/sign-in/basic/schemas/form';
import { login, getCurrentUser } from 'services/auth';
import { useMaterialUIController, setName, setEmail, setRole, setAbility, setClient, setUser } from 'context';
import { useToast } from 'context/ToastContext';
import { getUserAbilities } from 'config/ability';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import MDInput from 'components/MDInput';
import { login2Fa, turnOn2Fa } from 'services/2fa';
import { useCookies } from 'react-cookie';

// Toka logo
import tokaLogo from 'assets/images/toka-logo.svg';

function Basic() {
  const [, dispatch] = useMaterialUIController();
  const [isTwoFactor, setIsTwoFactor] = useState(false);
  const [code, setCode] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [values, setValues] = useState({ email: '', password: '' });
  const [cookie, setCookie] = useCookies(['access_token']);
  const [loginType, setLoginType] = useState('toka-team');
  const toast = useToast();
  const navigate = useNavigate();

  const handleSubmit = (values, actions) =>
    login(values.email, values.password)
      .then((res) => {
        if (res.data.isTwoFactorAuthenticationEnabled) {
          setIsTwoFactor(res.data.isTwoFactorAuthenticationEnabled);
          setValues(values);
          actions.setSubmitting(false);
          actions.resetForm();
        } else {
          setCookie('access_token', res?.data?.access_token, { path: '/' });
          actions.setSubmitting(false);
          actions.resetForm();
          if (res.data.access_token == undefined) {
            setErrorCode('Email or password is incorrect');
            toast.error('Email or password is incorrect');
          } else {
            getCurrentUser()
              .then((res) => {
                setUserData(res);
              })
              .catch((err) => {
                console.error(err);
                toast.error('Session validation failed. Please sign in again.');
              });
          }
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error('Email or password is incorrect');
        actions.setSubmitting(false);
        actions.resetForm();
      });

  function setUserData(user) {
    setName(dispatch, `${user.firstName} ${user.lastName}`);
    setEmail(dispatch, user.email);
    setRole(dispatch, user.role);
    setAbility(dispatch, getUserAbilities(user.role, user?.client));
    navigate('/dashboard', { replace: true });
  }

  function handleChange(e) {
    const currentCode = e.target.value;

    if (currentCode.length <= 6) {
      setCode(currentCode);

      if (errorCode) {
        setErrorCode('');
      }
    }
  }

  async function handleSubmitCode(e) {
    e.preventDefault();
    if (code?.length < 6) {
      return;
    }
    try {
      const result = await login2Fa(code, values.email, values.password);
      if (result.status == 200) {
        result.data.access_token && setCookie('access_token', result?.data?.access_token, { path: '/' });
        getCurrentUser()
          .then((user) => {
            setUserData(user);
          })
          .catch((err) => {
            console.error(err);
            toast.error('Session validation failed. Please sign in again.');
          });
      }
    } catch (error) {
      console.log(error);
      setErrorCode('INCORRECT AUTHENTICATION CODE!');
    }
  }
  const { formId, formField } = form;
  return (
    <BasicLayout>
      <Card
        sx={{
          background: 'rgba(17, 25, 40, 0.75)',
          backdropFilter: 'saturate(200%) blur(25px)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
        }}
      >
        {/* Top accent line */}
        <MDBox
          sx={{
            height: '3px',
            background: 'linear-gradient(90deg, #e2001b, #ff4f69, transparent 80%)',
            borderRadius: '12px 12px 0 0'
          }}
        />

        <MDBox pt={4} pb={3.5} px={4}>
          {/* Login Type Toggle */}
          <MDBox display='flex' justifyContent='center' mb={3}>
            <MDBox
              sx={{
                display: 'inline-flex',
                background: 'rgba(255,255,255,0.08)',
                borderRadius: '12px',
                padding: '4px',
                gap: '4px'
              }}
            >
              <MDBox
                onClick={() => setLoginType('toka-team')}
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: '10px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: loginType === 'toka-team' ? 'linear-gradient(135deg, #e2001b, #ff4f69)' : 'transparent',
                  '&:hover': {
                    background: loginType === 'toka-team' ? 'linear-gradient(135deg, #c8001a, #e2001b)' : 'rgba(255,255,255,0.05)'
                  }
                }}
              >
                <MDTypography
                  variant='button'
                  fontWeight={loginType === 'toka-team' ? 'bold' : 'regular'}
                  sx={{ color: loginType === 'toka-team' ? '#fff' : 'rgba(255,255,255,0.6)' }}
                >
                  Toka Team
                </MDTypography>
              </MDBox>
              <MDBox
                onClick={() => setLoginType('partner')}
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: '10px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: loginType === 'partner' ? 'linear-gradient(135deg, #e2001b, #ff4f69)' : 'transparent',
                  '&:hover': {
                    background: loginType === 'partner' ? 'linear-gradient(135deg, #c8001a, #e2001b)' : 'rgba(255,255,255,0.05)'
                  }
                }}
              >
                <MDTypography
                  variant='button'
                  fontWeight={loginType === 'partner' ? 'bold' : 'regular'}
                  sx={{ color: loginType === 'partner' ? '#fff' : 'rgba(255,255,255,0.6)' }}
                >
                  Partner
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>

          {/* Logo + Title */}
          <MDBox display='flex' flexDirection='column' alignItems='center' mb={3}>
            <MDBox
              component='img'
              src={tokaLogo}
              alt='Toka City'
              sx={{ height: 52, mb: 2 }}
            />
            <MDTypography variant='h5' fontWeight='bold' color='white'>
              {isTwoFactor ? 'Two-Factor Authentication' : loginType === 'partner' ? 'Partner Login' : 'Welcome Back'}
            </MDTypography>
            <MDTypography variant='body2' sx={{ color: 'rgba(255,255,255,0.5)', mt: 0.5, fontSize: '0.82rem' }}>
              {isTwoFactor ? 'Enter your 6-digit code' : loginType === 'partner' ? 'Partner portal access' : 'Sign in to your backoffice account'}
            </MDTypography>
          </MDBox>

          {/* Form */}
          <Grid container justifyContent='center' alignItems='center'>
            <Grid item xs={12}>
              {loginType === 'partner' ? (
                // Partner Login - Coming Soon
                <MDBox display='flex' flexDirection='column' alignItems='center' py={4}>
                  <Icon sx={{ fontSize: 64, color: 'rgba(255,255,255,0.3)', mb: 2 }}>lock_clock</Icon>
                  <MDTypography variant='h6' fontWeight='bold' color='white' mb={1}>
                    Coming Soon
                  </MDTypography>
                  <MDTypography variant='body2' sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', maxWidth: 320 }}>
                    Partner portal access is currently under development. Please check back soon or contact support for more information.
                  </MDTypography>
                </MDBox>
              ) : isTwoFactor ? (
                <Formik>
                  {() => (
                    <Form id='2fa' autoComplete='off'>
                      <MDBox className='remove-arrows-from-input'>
                        <MDInput
                          type='number'
                          label='Code'
                          fullWidth
                          value={code}
                          onChange={handleChange}
                          error={errorCode ? true : false}
                          InputProps={{
                            style: { color: '#fff' }
                          }}
                          InputLabelProps={{
                            style: { color: 'rgba(255,255,255,0.7)' }
                          }}
                          sx={{
                            '& .MuiInput-root': {
                              color: '#fff'
                            },
                            '& .MuiInput-root:before': {
                              borderBottomColor: 'rgba(255,255,255,0.2)'
                            },
                            '& .MuiInput-root:hover:before': {
                              borderBottomColor: 'rgba(255,255,255,0.4)'
                            },
                            '& .MuiInput-root:after': {
                              borderBottomColor: '#e2001b'
                            }
                          }}
                        />
                        <MDBox mt={0.75}>
                          <MDTypography component='div' variant='caption' color='error' fontWeight='regular'>
                            {errorCode}
                          </MDTypography>
                        </MDBox>
                        <MDBox mt={2.5} width='100%'>
                          <MDButton
                            disabled={code?.length >= 6 ? false : true}
                            onClick={handleSubmitCode}
                            type='submit'
                            fullWidth
                            sx={{
                              background: 'linear-gradient(135deg, #e2001b, #ff4f69)',
                              color: '#fff',
                              fontWeight: 700,
                              py: 1.2,
                              '&:hover': { background: 'linear-gradient(135deg, #c8001a, #e2001b)' },
                              '&:disabled': { opacity: 0.5 }
                            }}
                          >
                            Verify Code
                          </MDButton>
                        </MDBox>
                      </MDBox>
                    </Form>
                  )}
                </Formik>
              ) : (
                <Formik initialValues={initialValues} validationSchema={validations} onSubmit={handleSubmit}>
                  {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                    <Form id={formId} autoComplete='off'>
                      <MDBox>
                        <UserInfo
                          formData={{
                            values,
                            touched,
                            formField,
                            errors,
                            setFieldValue,
                            isSubmitting
                          }}
                        />
                        {errorCode && (
                          <MDTypography variant='caption' color='error' display='block' mt={1} textAlign='center'>
                            {errorCode}
                          </MDTypography>
                        )}
                        <MDBox mt={2.5} width='100%'>
                          <MDButton
                            disabled={isSubmitting}
                            type='submit'
                            fullWidth
                            sx={{
                              background: 'linear-gradient(135deg, #e2001b, #ff4f69)',
                              color: '#fff',
                              fontWeight: 700,
                              py: 1.2,
                              '&:hover': { background: 'linear-gradient(135deg, #c8001a, #e2001b)' },
                              '&:disabled': { opacity: 0.5 }
                            }}
                          >
                            {isSubmitting ? 'Signing in…' : 'Sign In'}
                          </MDButton>
                        </MDBox>
                      </MDBox>
                    </Form>
                  )}
                </Formik>
              )}
            </Grid>
          </Grid>

          {/* Footer text */}
          <MDBox mt={3} textAlign='center'>
            <MDTypography variant='caption' sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.68rem' }}>
              Toka City &middot; Backoffice
            </MDTypography>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
